
import { useState, useEffect } from "react";

interface UsePaymentRequestProps {
  stripe: any;
  elements: any;
  amount: string;
  paymentMethod: string;
  onSubmit: (paymentMethod: any) => Promise<void>;
}

export const usePaymentRequest = ({ 
  stripe, 
  elements, 
  amount, 
  paymentMethod, 
  onSubmit 
}: UsePaymentRequestProps) => {
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!stripe || !elements) return;

    // Reset state when payment method changes
    setPaymentRequest(null);
    setCanMakePayment(false);
    setIsProcessing(false);

    // Create PaymentRequest for Apple Pay or Google Pay
    const country = 'US';
    const currency = 'usd';
    
    console.log(`Creating PaymentRequest with country: ${country}, currency: ${currency} for method: ${paymentMethod}`);
    
    const pr = stripe.paymentRequest({
      country: country,
      currency: currency,
      total: {
        label: 'Metrade Pro Subscription',
        amount: parseFloat(amount) * 100, // Convert to cents
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    // Check if the Payment Request is available
    pr.canMakePayment().then((result: any) => {
      if (result) {
        console.log("Payment request result:", result);
        setPaymentRequest(pr);
        
        // Set canMakePayment based on payment method
        if (paymentMethod === 'google-pay' && result.googlePay) {
          setCanMakePayment(true);
          console.log("Google Pay is available");
        } else if (paymentMethod === 'apple-pay' && result.applePay) {
          setCanMakePayment(true);
          console.log("Apple Pay is available");
        }
      } else {
        console.log(`Payment method ${paymentMethod} is not available`);
      }
    }).catch((err: Error) => {
      console.error("Error checking payment availability:", err);
    });

    // Handle payment method when selected
    pr.on('paymentmethod', async (e: any) => {
      console.log("Payment method created:", e.paymentMethod);
      setIsProcessing(true);
      try {
        await onSubmit(e.paymentMethod);
        e.complete('success');
        console.log("Payment completed successfully");
      } catch (error) {
        console.error("Payment failed:", error);
        e.complete('fail');
      } finally {
        setIsProcessing(false);
      }
    });

    return () => {
      // Cleanup if needed
    };
  }, [stripe, elements, amount, onSubmit, paymentMethod]);

  return { paymentRequest, canMakePayment, isProcessing };
};
